'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Bell } from 'lucide-react'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/ui/shared/components/dropdown-menu'
import { NEWS_READ_STORAGE_KEY, NEWS_STORAGE_KEY, hasUnread, isUnread, type NewsItem } from '@/lib/cms/news'

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    })
}

export function NewsBell({ items }: { items: NewsItem[] }) {
    const [baseline, setBaseline] = useState<string | null>(null)
    const [readSlugs, setReadSlugs] = useState<string[]>([])
    // The dot depends on localStorage, which the server cannot know, so it is rendered
    // only after mount — otherwise hydration would mismatch.
    const [mounted, setMounted] = useState(false)
    // Set when the menu closes because a link was followed, so focus is not pulled back
    // to the bell on a page the visitor has already left.
    const navigatedRef = useRef(false)

    useEffect(() => {
        const stored = window.localStorage.getItem(NEWS_STORAGE_KEY)
        if (stored) {
            setBaseline(stored)
        } else {
            // A first-time visitor starts with a clean slate rather than a wall of unread.
            const now = new Date().toISOString()
            window.localStorage.setItem(NEWS_STORAGE_KEY, now)
            setBaseline(now)
        }
        try {
            const parsed = JSON.parse(window.localStorage.getItem(NEWS_READ_STORAGE_KEY) ?? '[]')
            if (Array.isArray(parsed)) setReadSlugs(parsed.filter(v => typeof v === 'string'))
        } catch {
            // Corrupt value: start over with nothing marked as read.
        }
        setMounted(true)
    }, [])

    if (!items.length) return null

    const markRead = (slug: string) => {
        navigatedRef.current = true
        if (readSlugs.includes(slug)) return
        const next = [...readSlugs, slug]
        window.localStorage.setItem(NEWS_READ_STORAGE_KEY, JSON.stringify(next))
        setReadSlugs(next)
    }

    const showDot = mounted && hasUnread(items, baseline, readSlugs)

    return (
        <DropdownMenu>
            {/* The open-state highlight is bound to data-state rather than to focus, the way
                Auphonic marks its bell: focus alone never leaves a stray outline behind. */}
            <DropdownMenuTrigger
                className="relative flex h-10 w-10 items-center justify-center rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                aria-label={showDot ? 'Новости, есть непрочитанные' : 'Новости'}
            >
                <Bell className="h-5 w-5" />
                {showDot && (
                    <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500" />
                )}
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                className="w-80 p-0"
                onCloseAutoFocus={event => {
                    // Radix restores focus to the trigger on close, which is right for
                    // Escape or a click outside but leaves a stray focus ring on the bell
                    // once a link has taken the visitor elsewhere.
                    if (!navigatedRef.current) return
                    navigatedRef.current = false
                    event.preventDefault()
                }}
            >
                <p className="border-b px-4 py-3 text-center text-sm font-semibold">Что нового</p>

                <div className="py-1">
                    {items.map(item => {
                        const unread = mounted && isUnread(item, baseline, readSlugs)
                        return (
                            // asChild makes the link itself the menu item, so selecting it
                            // closes the menu — a plain link would leave it open, because
                            // client-side navigation never unmounts it.
                            <DropdownMenuItem key={item.slug} asChild className="cursor-pointer">
                                <Link
                                    href={`/${item.slug}`}
                                    className="flex items-start gap-2 px-4 py-2"
                                    onClick={() => markRead(item.slug)}
                                >
                                    <span
                                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${unread ? 'bg-red-500' : 'bg-gray-300'}`}
                                        aria-hidden="true"
                                    />
                                    <span className="min-w-0">
                                        <span className={`block text-sm ${unread ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                                            {item.title}
                                        </span>
                                        <span className="block text-xs text-gray-400">
                                            {formatDate(item.publishedAt)}
                                        </span>
                                    </span>
                                </Link>
                            </DropdownMenuItem>
                        )
                    })}
                </div>

                <div className="border-t p-1">
                    <DropdownMenuItem asChild className="cursor-pointer">
                        <Link
                            href="/updates"
                            className="px-4 py-2 text-sm text-[var(--link)]"
                            onClick={() => { navigatedRef.current = true }}
                        >
                            Все обновления
                        </Link>
                    </DropdownMenuItem>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
